import {
  AltiusRes,
  IUpdateStatusReq,
  TaskCreate,
  TaskPreview,
  TaskQueries,
  TaskQueryParams,
  TaskStatus
} from "store/types";
import rmEmptyFields from "utils/rm-empty-fields";
import { TaskManageAction } from "store/actions";
import { AsyncActions } from "../base";
import { toFormData } from "utils";

/** Shape of the filters that the ClientTasksTab component passes to fetchClientTasksAction. */
export interface ClientTasksFilters {
  status?: TaskStatus | null;
  assignee_id?: number | null;
  search?: string;
  page?: number;
  per_page?: number;
}

export interface IClientTasksActions {
  /** Fetch tasks for a specific client with optional filters. */
  fetchClientTasksAction: (clientId: number, filters?: ClientTasksFilters) => Promise<void>;
  /** Update the status of a single task (open → in-progress → done → open). */
  updateTaskStatusAction: (taskId: number, newStatus: TaskStatus) => Promise<void>;
  /** Open the Add Task modal pre-selected for a client. */
  setCreateAction: (data: TaskCreate) => void;
}

export class ClientTasksActions extends AsyncActions implements IClientTasksActions {
  /**
   * Build API query parameters from the client ID and optional filters.
   */
  private buildParams = (clientId: number, filters?: ClientTasksFilters): TaskQueryParams => {
    const params: TaskQueryParams = {
      entity_id: clientId,
      entity_type: "client"
    };

    if (!filters) return params;

    if (filters.status) {
      params.statuses = [filters.status];
    }

    if (filters.assignee_id) {
      params.assignee_ids = [filters.assignee_id];
    }

    if (filters.search) {
      params.search = filters.search;
    }

    if (filters.page) {
      params.page = filters.page;
      params.per_page = filters.per_page ?? 10;
    }

    return params;
  };

  /**
   * Fetch tasks for a given client, applying optional filters.
   * Calls GET /api/v0.0.3/tasks?entity_id={clientId}&entity_type=client
   */
  public fetchClientTasksAction = async (clientId: number, filters?: ClientTasksFilters): Promise<void> => {
    const params = this.buildParams(clientId, filters);
    const {
      data: { data: payload }
    } = await this.api.get<AltiusRes<TaskPreview[]>>({
      url: "/api/v0.0.3/tasks",
      params
    });
    this.dispatch({
      type: TaskManageAction.GetTasksSuccess,
      payload
    });
  };

  /**
   * Update the status of a task (cycle: open → in-progress → done → open).
   * Calls POST /api/v0.0.3/tasks/{taskId}/status with _method: PATCH.
   */
  public updateTaskStatusAction = async (taskId: number, newStatus: TaskStatus): Promise<void> => {
    const {
      data: { data }
    } = await this.api.post<AltiusRes<TaskPreview>>({
      url: `/api/v0.0.3/tasks/${taskId}/status`,
      data: toFormData({
        _method: "PATCH",
        status: newStatus
      }),
      headers: {
        "Content-Type": "multipart/form-data"
      }
    });
    this.dispatch({
      type: TaskManageAction.UpdateTaskSuccess,
      payload: data
    });
  };

  /**
   * Open the Add Task modal pre-selected for a client entity.
   * Dispatches SetCreate to the task manager reducer.
   */
  public setCreateAction = (payload: TaskCreate): void => {
    this.dispatch({ type: TaskManageAction.SetCreate, payload });
  };
}
